import { Css, useTestIds } from "@homebound/beam";
import { formatToPrice } from "src/components";
import { ConfigOptionsAndDelta } from "../AddProductOfferingConfigPage";
import { GroupedOptionsProps } from "./OptionsByOptionGroupSection";
import { OptionImageOrPlaceholder } from "./OptionSection";

export type CostSummarySideBarProps = {
  groupedRposByType: Record<string, ConfigOptionsAndDelta[]>;
  totalCostInCents: number;
};

export function CostSummarySideBar({ groupedRposByType, totalCostInCents }: CostSummarySideBarProps) {
  return (
    <div css={Css.df.fdc.gap3.$}>
      <h1 css={Css.xl.gray900.$}>Cost Summary</h1>
      <p css={Css.gray700.sm.$}>
        Costs summaries are pulled from a mixture of cost sources, including some that are not awarded bids.
      </p>
      {Object.entries(groupedRposByType).map(([optGroupName, optGroupRpos]) => (
        <div key={[optGroupName, ...optGroupRpos.map((delta) => delta.rpo.id)].join("-")} css={Css.df.fdc.gap3.$}>
          {optGroupRpos
            .sortBy((data) => data.rpo.order ?? Number.POSITIVE_INFINITY)
            .map((data) => (
              <CostSummaryOptionCard key={data.rpo.id} configData={data} />
            ))}
        </div>
      ))}
      <div css={Css.df.jcsb.aic.$}>
        <div css={Css.smSb.gray900.$}>Total Cost</div>
        <div css={Css.lg.gray900.$}>{formatToPrice({ valueInCents: totalCostInCents })}</div>
      </div>
    </div>
  );
}

export function CostSummaryOptionCard(
  props: Omit<GroupedOptionsProps, "groupedRpos" | "formState" | "recomputeCosts"> & {
    configData: ConfigOptionsAndDelta;
  },
) {
  const { configData } = props;
  const { rpo, delta } = configData;

  const tid = useTestIds({});

  // For design packages, combine the type and option group name such as `Design Package - Interior Style` since the groups no longer have subheadings
  const typeDisplayname =
    (rpo.type.forDesignInterior || rpo.type.forDesignExterior) && rpo.type.name !== rpo.optionGroup.name
      ? `${rpo.type.name} - ${rpo.optionGroup.name}`
      : rpo.type.name;

  return (
    <div css={Css.df.jcsb.gap2.$}>
      <OptionImageOrPlaceholder rpo={rpo} disabled={false} onCostSummary />
      <div css={Css.df.fdc.w100.$}>
        <div css={Css.sm.gray700.xs2Sb.$}>{typeDisplayname}</div>
        <div css={Css.df.jcsb.xsSb.gray900.$}>
          <div>{[rpo.name, rpo.location?.name].compact().join(" - ")}</div>
          <div {...tid.opt}>{formatToPrice({ valueInCents: delta?.totalCostInCents })}</div>
        </div>
      </div>
    </div>
  );
}
